import { z } from 'zod';
import { CharacterRigSchema } from './rig';
import { Model3DMorphPackSchema, Model3DTexturePackSchema } from './model3d-pack';
import { GameProfileSchema } from './game-bridge';
import { ProceduralActionSchema, PROCEDURAL_ACTIONS_MAX } from './procedural-actions';
const idPattern = /^[a-z0-9_-]+$/;
const slugPattern = /^[a-z0-9-]{3,40}$/;
const draftSlugPattern = /^[a-z0-9-]*$/;
/**
 * Per-frame features derived from MediaPipe FaceLandmarker during import.
 * Used by Motion Matching at runtime to pick the best frame for a target
 * state (mouth openness, gaze direction, etc.) instead of blindly cycling.
 *
 * All values are normalised to [-1, 1] or [0, 1] so they compare sanely
 * across frames of different sizes. If a feature can't be computed (e.g.
 * no face detected), the whole record is `null` at that position.
 */
const PoseFeatureSchema = z.object({
    /** Mouth openness 0..1 (vertical lip distance / face height). */
    mouthOpen: z.number().min(0).max(1),
    /** Average eye openness 0..1 (eyelid distance / eye width). */
    eyeOpen: z.number().min(0).max(1),
    /** Head yaw -1..1 (−1 looking left, +1 right). Approximate from nose offset. */
    headYaw: z.number().min(-1).max(1),
    /** Head pitch -1..1 (−1 looking down, +1 up). Approximate. */
    headPitch: z.number().min(-1).max(1),
    /** Optional expression tag assigned manually or by classifier. */
    expression: z.string().max(40).optional(),
}).nullable();
const LayerSchema = z.object({
    blobPath: z.string().min(1),
    /**
     * Additional frames beyond blobPath. Full frame sequence = [blobPath, ...frames].
     * When present and non-empty, the layer animates while visible (cycles at `fps`).
     * Optional for backward compatibility with single-frame layers.
     */
    frames: z.array(z.string().min(1)).optional(),
    /** Playback rate for the frame sequence. Ignored for single-frame layers. */
    fps: z.number().int().min(1).max(60).optional(),
    /** What happens after the last frame. Ignored for single-frame layers. */
    loop: z.enum(['loop', 'pingpong', 'once']).optional(),
    /**
     * Motion Matching pose features — parallel to [blobPath, ...frames].
     * `poseFeatures[0]` describes `blobPath`, `poseFeatures[i+1]` describes `frames[i]`.
     * When present, the runtime can pick the frame that best matches a target
     * state (e.g. "find a frame with mouthOpen ≈ 0.7") instead of cycling.
     */
    poseFeatures: z.array(PoseFeatureSchema).optional(),
});
const StateSchema = z.object({
    compose: z.array(z.string().min(1)).min(1),
    idleMotion: z.enum(['none', 'subtle', 'tilt']).default('none'),
    label: z.string().min(1).max(40),
    description: z.string().max(200).default(''),
    llmSelectable: z.boolean().default(true),
});
/** Character renderer type. Discriminates which payload block drives rendering. */
export const CharacterTypeEnum = z.enum(['pngtuber', 'live2d', 'model3d']);
/**
 * Per `fan-youtube-integration` — lifecycle of the per-channel
 * member-detection feature. Set/transitioned by the operator OAuth
 * flow + members poller; read by the fan SPA to decide whether the
 * membership endpoint can return a real signal.
 */
export const MembersFeatureStatusEnum = z.enum([
    'disabled', // never enabled by operator
    'pending-first-poll', // operator just OAuth'd; first poll not yet run
    'active', // poller running, cache populated
    'not-eligible', // channel doesn't have YPP memberships enabled
    'token-revoked', // operator's Google refresh token failed exchange
]);
/**
 * Per `fan-youtube-integration` — character→YouTube-channel binding.
 * Bare `channelId` is enough to link to YT; `membersFeatureStatus`
 * transitions only when the operator additionally completes the
 * creator-scope OAuth flow.
 */
export const YouTubeChannelBindingSchema = z.object({
    /** UCxxxxxxxxxxxxxxxxxxxxxx (24 chars total, starts with "UC"). */
    channelId: z.string().regex(/^UC[A-Za-z0-9_-]{22}$/, 'YouTube 頻道 ID 必須以 UC 開頭並有 24 字元'),
    /** Optional @handle for display; informational only. */
    channelHandle: z.string().regex(/^@[A-Za-z0-9._-]{1,30}$/).optional(),
    membersFeatureStatus: MembersFeatureStatusEnum.default('disabled'),
    /** ISO timestamp when the poller last completed a cycle for this channel. */
    lastPolledAt: z.string().datetime().optional(),
});
/**
 * Live2D (Cubism) payload. Populated when `type === 'live2d'`.
 * Absent for PNGTuber and 3D characters.
 */
const Live2DPayloadSchema = z.object({
    modelPath: z.string().default(''),
    mouthBinding: z.string().max(80).default('ParamMouthOpenY'),
    parameters: z.record(z.string(), z.number()).default({}),
});
// ── Scene objects (per `scene-objects-and-attachments` spec) ──────────
// Auxiliary 3D models loaded alongside the character mesh:
//  - static: world-anchored scene props (chairs, tables)
//  - dynamic: parented to a character bone; follows skeleton animation
//
// Visibility:
//  - always: render from mount (e.g. permanent glasses)
//  - ai-controlled: hidden until the chat handler toggles via
//    `{ type: 'object', id, visible: true }` in the action plan
//
// Caps: ≤16 per character; aiDescription required when ai-controlled;
// attachment.boneName required (and only set) when kind === 'dynamic'.
// Finite-only number, capped at ±10000. Used for SceneObject transform
// (position / rotation in radians / scale) and collider AABB. Fans can
// write into model3d.sceneObjects via the attach-to-character endpoint —
// without these guards a fan could submit NaN / ±Infinity / 1e308 to
// corrupt the shared character record (up to 16 sceneObjects per record,
// visible to every viewer of that character). Bounds are generous enough
// for any legitimate use: 10 km / >1500 full rotations / 10000× scale.
const FiniteCoord = z.number().refine((n) => Number.isFinite(n) && n >= -10000 && n <= 10000, { message: '座標必須為有限數值且介於 ±10000' });
const Vec3 = z.tuple([FiniteCoord, FiniteCoord, FiniteCoord]);
export const SceneObjectSchema = z.object({
    id: z.string().min(1).max(64),
    label: z.string().min(1).max(40),
    modelPath: z.string().min(1),
    modelFormat: z.enum(['glb', 'fbx']),
    kind: z.enum(['static', 'dynamic']),
    visibility: z.enum(['always', 'ai-controlled']),
    /** Required when visibility === 'ai-controlled'. Folded into chat system prompt. */
    aiDescription: z.string().min(1).max(200).optional(),
    transform: z.object({
        position: Vec3,
        rotation: Vec3,
        scale: Vec3,
    }),
    /** Required when kind === 'dynamic'. Bone name from the character rig. */
    attachment: z.object({
        boneName: z.string().min(1).max(80),
    }).optional(),
    /** Auto-set on save based on whether the loaded model has animation tracks. */
    hasOwnAnimation: z.boolean().optional(),
    /** World-space (static) or bone-local (dynamic) AABB; captured at first load. */
    collider: z.object({
        min: Vec3,
        max: Vec3,
    }).optional(),
    /**
     * Per `shared-object-library` spec. When set, this entry references a
     * shared-object-library item (cross-character reusable). Drives delete
     * cascade:
     *   - undefined → character-local upload. Deleting this entry MUST
     *     also remove the S3 file at `modelPath` (the character owns it).
     *   - non-empty   → library reference. Deleting this entry only removes
     *     the array slot; the S3 file at `_library/<libraryId>/...` and
     *     the `nx-shared-objects-<env>` DDB record stay intact for other
     *     characters that still reference it.
     * `modelPath` is always set so deploy-time bake + runtime loader stay
     * unchanged — the runtime never has to resolve libraryId itself.
     */
    libraryId: z.string().min(1).max(80).optional(),
    /**
     * Optional override texture for the object. When set, the runtime
     * loads this image and assigns it as `material.map` on every mesh
     * inside the loaded GLB — overriding the GLB's baked-in materials.
     * Unlike character textures, this is a single texture per object
     * (no expression / viseme switching), so operators just upload one
     * PNG / JPG and it skins the whole object.
     *
     * Stored on S3 like every other character asset; URL is the
     * CloudFront-served public path.
     */
    texture: z.string().url().optional(),
    /**
     * Procedural shader actions for this object (currently breath only;
     * mirrors `character.model3d.proceduralActions`). Each entry's GLSL
     * is injected via `material.onBeforeCompile` into every mesh in the
     * loaded GLB, so a static object can "breathe" without a skeleton.
     * Bounds are computed from the object's own AABB at attach time.
     */
    proceduralActions: z.array(ProceduralActionSchema).max(PROCEDURAL_ACTIONS_MAX).default([]),
});
export const Model3DMaterialOverrideSchema = z.object({
    meshName: z.string().max(120).optional(),
    materialName: z.string().min(1).max(120),
    type: z.enum(['auto', 'opaque', 'faceOverlay', 'cutout', 'hair', 'shadow']).optional(),
    transparent: z.boolean().optional(),
    alphaTest: z.number().min(0).max(1).optional(),
    depthWrite: z.boolean().optional(),
    depthTest: z.boolean().optional(),
    renderOrder: z.number().int().min(-100).max(100).optional(),
    side: z.enum(['front', 'back', 'double']).optional(),
});
/**
 * 3D (GLB/FBX) payload. Populated when `type === 'model3d'`.
 * Absent for PNGTuber and Live2D characters.
 */
const Model3DPayloadSchema = z.object({
    modelPath: z.string().default(''),
    modelFormat: z.enum(['glb', 'fbx']).optional(),
    mouthBinding: z.union([
        z.object({ kind: z.literal('morph'), target: z.string() }),
        z.object({ kind: z.literal('bone'), name: z.string(), axis: z.enum(['x', 'y', 'z']).default('x') }),
        // lts_o-style shader: swap the material's diffuse map between pre-baked
        // mouth-shape textures. `base` is mandatory (closed/default); AEIOU
        // vowels are optional — missing keys fall back to base.
        z.object({
            kind: z.literal('texture'),
            textures: z
                .object({
                base: z.string(),
                A: z.string().optional(),
                E: z.string().optional(),
                I: z.string().optional(),
                O: z.string().optional(),
                U: z.string().optional(),
            }),
        }),
        z.object({ kind: z.literal('none') }),
    ]).optional(),
    /** Map from declared state name to AnimationClip name. */
    animationMap: z.record(z.string(), z.string()).default({}),
    /**
     * Operator-defined shader-driven idle motion (vertex displacement)
     * that runs without a skeleton. Each entry is a discriminated-union
     * `ProceduralAction` keyed on `type`; `breath` is the first
     * supported type. Designed for mascot / no-skeleton models that
     * can't ship animation clips. Capped at 8 per character. Invisible
     * to the LLM — purely idle effect, not in `Model3DInventory`.
     */
    proceduralActions: z.array(ProceduralActionSchema).max(PROCEDURAL_ACTIONS_MAX).default([]),
    /** Camera pose (position + target) persisted across sessions; shape opaque here. */
    pose: z.record(z.string(), z.number()).optional(),
    /** Operator-uploaded static thumbnail for gallery cards. When absent,
     *  admin/fan galleries fall back to rendering the model preview. */
    thumbnailUrl: z.string().url().optional(),
    /**
     * Shader-composed texture pack (base + masks + 5 visemes + expression PNGs).
     * Optional on read so legacy records without a pack survive; the tolerant
     * read path in `repo.ts` substitutes OL_SAMPLE_PACK when missing. The
     * runtime requires it at mount time and rejects when absent.
     */
    texturePack: Model3DTexturePackSchema.optional(),
    /**
     * Blend-shape / morph-target expression pack. Imported from the
     * standalone Three.js viewer's `*.expressions.json` format:
     * expression presets contain binds that write
     * `mesh.morphTargetInfluences[index] = weight`.
     *
     * Mutually exclusive by convention with texturePack-driven facial
     * control; `expressionMode` is the runtime hint used by admin tabs.
     */
    morphPack: Model3DMorphPackSchema.optional(),
    expressionMode: z.enum(['texturePack', 'morphTargets']).optional(),
    /**
     * Naming convention the operator picked when uploading the model:
     *   - `'v2'` (default) — Malfeuera-style: `Eye_blink.png`, `Mouse_A.png`, `Eye_smile.png`
     *   - `'v1'` — OL-style: `A.jpg`, `E.jpg`, `base.JPEG`, `Close.png`
     * Used by the auto-detect / texture-mapping UIs to know which
     * filename patterns to expect. **No path-heuristic** is used — the
     * operator picks at upload time, default `'v2'`. Pre-existing
     * characters without this field default to `'v2'` on read.
     */
    textureConvention: z.enum(['v1', 'v2']).optional(),
    /** Default expression id used on first mount (must match a texturePack.expressions[].id). */
    defaultExpression: z.string().max(40).optional(),
    /** Default animation clip name used on first mount (must match an AnimationClip on the model). */
    defaultAnimation: z.string().max(80).optional(),
    /**
     * AI-visible labels per animation clip. Maps clipName → human-readable
     * label (e.g., `{ 'NlaTrack.005': '歡呼' }`). Empty / missing values mean
     * "AI cannot see this clip". Mirror of the prototype-page localStorage
     * `nxorbit-3d-local-clip-labels` shape, persisted server-side.
     */
    clipLabels: z.record(z.string(), z.string().max(40)).optional(),
    /** Subset of `clipLabels` values treated as default-loop animations. */
    defaultClipLabels: z.array(z.string().max(40)).optional(),
    /** Subset of `clipLabels` values treated as one-shot animations. */
    oneShotClipLabels: z.array(z.string().max(40)).optional(),
    /**
     * Per-label playback repetition for one-shot animations. Maps
     * `oneShotClipLabels` value → integer in `{1, 2, 3}`. Sparse —
     * absent label = 1 play (existing behavior). Anything outside
     * 1/2/3 is rejected at parse time so a stray UI bug can't smuggle
     * unbounded loops into the wire payload.
     */
    oneShotLoopCounts: z.record(z.string(), z.union([z.literal(1), z.literal(2), z.literal(3)])).optional(),
    /**
     * Per-expression hold duration in seconds. Maps expression id (e.g.
     * `smile`, `cry`) to seconds before the runtime auto-returns to
     * Nature. Sparse — entries omitted means "sticky" (no auto-return).
     * Range 0–10 s; 0 also means sticky. Mirrors the natural "duration"
     * that animation clips have via their GLB timeline so the AI can
     * plan an emotional beat the same way it picks a one-shot clip.
     */
    expressionDurations: z.record(z.string().max(40), z.number().min(0).max(10)).optional(),
    /**
     * Per-expression natural-language description of WHEN to use it.
     * Maps expression id (e.g. `kira`) to a human-written hint that the
     * chat handler folds into the system prompt's `expressionEnum`,
     * giving the LLM context like "kira: 極度興奮 / 看到喜歡的東西時眼睛
     * 發亮". Texture filenames rarely match the trigger semantics, so
     * the operator describes them here. Sparse — entries omitted means
     * "id alone is the only hint the LLM gets" (legacy behaviour).
     * Stored on the character (not the texturePack) because the same
     * sample pack is shared across many characters and each character's
     * personality may use the same expression slot for different beats.
     */
    expressionDescriptions: z.record(z.string().max(40), z.string().max(200)).optional(),
    /**
     * Per `embed-thinking-visual-feedback` spec: which expression id
     * to apply during the LLM-thinking gap (between user-utterance-end
     * and AI-reply-start). Should match an entry in
     * `texturePack.expressions`; runtime soft-validates and warns if
     * absent. Auto-reverts to Nature when the AI starts speaking.
     */
    thinkingExpression: z.string().max(40).optional(),
    /**
     * Per `embed-thinking-visual-feedback` spec: which clip label to
     * loop during the thinking gap. Should match a key in
     * `clipLabels`. Auto-reverts to the configured default-loop when
     * the AI starts speaking.
     */
    thinkingClip: z.string().max(40).optional(),
    /**
     * Per `embed-thinking-visual-feedback` spec: optional URL of a
     * short loop-friendly audio asset (breath, hmm) played at low
     * volume during the thinking gap. Operator uploads via the Voice
     * tab through the existing presigned-PUT flow. Absent = silent.
     */
    thinkingSoundUrl: z.string().url().optional(),
    /**
     * Persisted runtime parameters mirroring the prototype's six live sliders.
     * Sparse — only the subfields the user actually touched are stored.
     * Absent subfields fall back to the documented defaults at runtime mount.
     */
    renderParams: z
        .object({
        mouthFadeSpeed: z.number().min(1).max(30).optional(),
        mouthOpacity: z.number().min(0).max(1).optional(),
        blinkMinInterval: z.number().min(0.8).max(8).optional(),
        blinkMaxInterval: z.number().min(1).max(10).optional(),
        blinkDuration: z.number().min(0.08).max(0.5).optional(),
        expressionFadeDuration: z.number().min(0).max(2).optional(),
        visemeDriftRate: z.number().min(0).max(20).optional(),
        mouthVisemeFadeDuration: z.number().min(0).max(0.5).optional(),
        mouthHoldDuration: z.number().min(0).max(2).optional(),
    })
        .optional(),
    /**
     * Persisted viewport pose — currently only `rotationY` (Euler Y in
     * degrees, applied to the loaded model root after frameModel). Some
     * GLBs ship facing +X or -Z; this slider lets the admin face the
     * model toward the camera and persist that orientation.
     */
    viewportPose: z
        .object({
        rotationY: z.number().min(-180).max(180).optional(),
    })
        .optional(),
    /**
     * When true (default), the runtime automatically reverts to the
     * Nature expression after dispatching an action plan that contains
     * no `expression` action — keeps the eyes "open" between
     * conversational beats. Set to false for sticky-expression characters
     * (e.g., a perpetually-angry NPC).
     */
    autoReturnToNature: z.boolean().optional(),
    materialOverrides: z.array(Model3DMaterialOverrideSchema).max(80).optional(),
    /**
     * Per `scene-objects-and-attachments` spec — optional auxiliary 3D
     * models loaded into the same scene as the character. `kind: 'static'`
     * is anchored in world space; `kind: 'dynamic'` is parented to a
     * named bone in the character rig (hat on head, baseball bat in
     * hand). `visibility: 'always'` renders at mount; `'ai-controlled'`
     * starts hidden and toggles via the chat handler's action plan.
     *
     * Cross-field constraints enforced in refine():
     *   - dynamic ↔ attachment.boneName required
     *   - static rejects attachment
     *   - ai-controlled ↔ aiDescription required
     * Hard cap of 16 entries.
     */
    sceneObjects: z.array(SceneObjectSchema).max(16).optional(),
    /**
     * Per `stage-livestudio` change — optional clip-label slots the Stage
     * (operator livestream backstage) uses to know which AnimationClip to
     * play for each locomotion role. Each value should match a clip name
     * in `animationMap` / on the GLB. ADDITIVE + OPTIONAL: absent slots
     * fall back to a label-name convention (`Walk` / `Sit_Idle` /
     * `Lie_Idle` / `Idle`) at runtime, and characters without this block
     * keep working unchanged (no migration). Not exposed to the LLM.
     */
    stageClips: z
        .object({
        walk: z.string().max(80).optional(),
        /** stage-broadcast-space — run locomotion clip (fast movement);
         *  falls back to `walk` when absent. */
        run: z.string().max(80).optional(),
        sit: z.string().max(80).optional(),
        lie: z.string().max(80).optional(),
        idle: z.string().max(80).optional(),
        /**
         * Free-form semantic-action → clip-label map for triggers beyond the
         * four locomotion roles (e.g. `{ wave: 'Wave_01', dance: 'Dance' }`).
         * The Stage's `playAction(name)` resolves via this map, falling back
         * to treating `name` as a literal clip label. Operator-authored in
         * the character editor (8.5 UI). Capped to keep payloads sane.
         */
        actions: z.record(z.string().max(40), z.string().max(80)).optional(),
    })
        .optional(),
});
// ── Agentic graph (slice 1: schema + validation only, no executor) ──────
//
// Per-character DAG of agentic-AI nodes. Stored on the character record
// so subsequent slices (executor, RAG node, tool node, SSE pills, graph
// editor) can land additively against a stable shape. v1 leaves
// `nodes[].config` open-ended — discriminated unions per type land when
// the executor reveals the real requirements (slice 2).
const agenticNodeIdPattern = /^[a-z][a-z0-9-]*$/;
export const AgenticNodeTypeEnum = z.enum(['llm-call', 'rag-lookup', 'tool-invoke', 'merge']);
export const AgenticGraphSchema = z.object({
    /** Gate flag the chat handler reads. When false / absent, legacy single-call path runs. */
    enabled: z.boolean(),
    nodes: z
        .array(z.object({
        id: z.string().regex(agenticNodeIdPattern, 'node id must be kebab-case'),
        type: AgenticNodeTypeEnum,
        // Open-ended in v1 — slice 2 narrows per type.
        config: z.record(z.string(), z.unknown()),
    }))
        .min(1, 'at least one node required'),
    edges: z.array(z.object({ from: z.string(), to: z.string() })),
    /** Id of the entry node. Must reference a real node id. */
    entry: z.string(),
    /** Ids of accepting nodes. At least one. Each must reference a real node id. */
    exits: z.array(z.string()).min(1, 'at least one exit required'),
});
/**
 * Pure DAG validator. Runs the structural checks the schema's
 * `superRefine` block can't express directly:
 *   - R-V1 unique node ids
 *   - R-V2 entry references an existing node
 *   - R-V3 every exit id references an existing node
 *   - R-V4 every edge endpoint references an existing node
 *   - R-V5 no cycles (Kahn's topo sort — the leftover set names the
 *          actual cycling nodes for the error message)
 *
 * Exported standalone so the executor (slice 2) and any future tooling
 * can run the same validation without going through zod.
 */
export function validateAgenticDag(graph) {
    const issues = [];
    // R-V1 — unique ids
    const seen = new Set();
    const dups = new Set();
    for (const n of graph.nodes) {
        if (seen.has(n.id))
            dups.add(n.id);
        seen.add(n.id);
    }
    if (dups.size > 0) {
        issues.push({ path: ['nodes'], message: `duplicate node ids: ${[...dups].join(', ')}` });
    }
    const idSet = seen;
    // R-V2 — entry refs an existing node
    if (!idSet.has(graph.entry)) {
        issues.push({ path: ['entry'], message: `entry "${graph.entry}" does not match any node id` });
    }
    // R-V3 — each exit refs an existing node
    graph.exits.forEach((exitId, i) => {
        if (!idSet.has(exitId)) {
            issues.push({ path: ['exits', i], message: `exit "${exitId}" does not match any node id` });
        }
    });
    // R-V4 — each edge endpoint refs an existing node
    graph.edges.forEach((e, i) => {
        if (!idSet.has(e.from)) {
            issues.push({ path: ['edges', i, 'from'], message: `edges[${i}].from "${e.from}" does not match any node id` });
        }
        if (!idSet.has(e.to)) {
            issues.push({ path: ['edges', i, 'to'], message: `edges[${i}].to "${e.to}" does not match any node id` });
        }
    });
    // R-V5 — cycle detection via Kahn's topological sort. We only run this
    // when the edge endpoints all resolved (otherwise the indegree counts
    // would be misleading). If R-V4 already added issues, skip; the cycle
    // message would just be noise on top of the dangling-edge errors.
    if (issues.every((i) => i.path[0] !== 'edges')) {
        const indegree = new Map();
        for (const n of graph.nodes)
            indegree.set(n.id, 0);
        for (const e of graph.edges) {
            indegree.set(e.to, (indegree.get(e.to) ?? 0) + 1);
        }
        const adj = new Map();
        for (const n of graph.nodes)
            adj.set(n.id, []);
        for (const e of graph.edges)
            adj.get(e.from).push(e.to);
        const queue = [];
        for (const [id, deg] of indegree) {
            if (deg === 0)
                queue.push(id);
        }
        let visited = 0;
        while (queue.length > 0) {
            const id = queue.shift();
            visited++;
            for (const next of adj.get(id) ?? []) {
                const deg = indegree.get(next) - 1;
                indegree.set(next, deg);
                if (deg === 0)
                    queue.push(next);
            }
        }
        if (visited < graph.nodes.length) {
            const cycling = [...indegree.entries()].filter(([, d]) => d > 0).map(([id]) => id);
            issues.push({ path: ['edges'], message: `cycle detected: nodes [${cycling.join(', ')}] form a cycle` });
        }
    }
    return issues;
}
// ── embed-conversation-shortcuts ──────────────────────────────────────
// Per-character quick-reply shortcuts surfaced in the public embed as
// a corner FAB → category strip → item list. See OpenSpec change
// `embed-conversation-shortcuts` for the full requirement set.
//
// Caps are tunable per-character (default 4 categories × 8 items, hard
// upper bound 6 × 12). Absent block means "no shortcuts" — embed
// renders no FAB.
/** Preset icon enum — 30 monochrome line-style SVGs shipped in admin-ui. */
export const PRESET_ICON_IDS = [
    'heart', 'star', 'gift', 'chat', 'question',
    'phone', 'map', 'cart', 'search', 'calendar',
    'wallet', 'game', 'book', 'music', 'camera',
    'settings', 'list', 'user', 'trophy', 'target',
    'mail', 'bell', 'globe', 'pin', 'lightbulb',
    'shopping', 'package', 'palette', 'sparkle', 'fire',
];
export const ShortcutIconSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('preset'), id: z.enum(PRESET_ICON_IDS) }),
    z.object({ kind: z.literal('custom'), url: z.string().url().max(500) }),
]);
export const ShortcutItemSchema = z.object({
    id: z.string().min(1).max(64),
    /** What the user SEES on the chip. Terse. */
    title: z.string().min(1).max(16),
    /** What gets DISPATCHED as the user's message to the LLM. Can be longer. */
    description: z.string().min(1).max(400),
});
export const ShortcutCategorySchema = z.object({
    id: z.string().min(1).max(64),
    /** A11y label for the icon-only category chip. */
    label: z.string().min(1).max(20),
    icon: ShortcutIconSchema,
    items: z.array(ShortcutItemSchema).max(12),
});
export const ShortcutsSchema = z.object({
    enabled: z.boolean().default(true),
    position: z.enum(['top-left', 'top-right', 'bottom-left', 'bottom-right']).default('top-right'),
    /** Operator-configured cap; hard upper bound 6 enforced here. Default 4. */
    maxCategories: z.number().int().min(1).max(6).default(4),
    /** Operator-configured cap; hard upper bound 12 enforced here. Default 8. */
    maxItemsPerCategory: z.number().int().min(1).max(12).default(8),
    categories: z.array(ShortcutCategorySchema).max(6),
});
const baseShape = z.object({
    id: z.string().uuid(),
    slug: z.string().regex(draftSlugPattern).max(40),
    name: z.string().min(1).max(60),
    description: z.string().max(500).default(''),
    /** Per `character-energy-system` — operator-uploaded idle screen
     *  (image/GIF) shown when a fan leaves the chat open & idle. Set in
     *  the admin character-edit 粉絲 tab. */
    idleScreen: z.object({ imageUrl: z.string().url().max(500) }).optional(),
    /**
     * Character renderer type. Optional on read (tolerant for pre-migration
     * records — defaults to 'pngtuber'). Task 9.1 will make this required
     * once backfill completes in every environment.
     */
    type: CharacterTypeEnum.default('pngtuber'),
    /** Live2D payload — present iff type === 'live2d'. */
    live2d: Live2DPayloadSchema.optional(),
    /** 3D payload — present iff type === 'model3d'. */
    model3d: Model3DPayloadSchema.optional(),
    manifestVersion: z.literal(1),
    viewport: z.object({
        width: z.number().int().positive().max(4096),
        height: z.number().int().positive().max(4096),
    }),
    layers: z.record(z.string().regex(idPattern), LayerSchema),
    states: z.record(z.string().regex(idPattern), StateSchema),
    initialState: z.string(),
    blink: z.object({
        intervalMs: z.tuple([z.number().int().min(200), z.number().int().min(200)]),
        durationMs: z.number().int().min(50).default(120),
        replaces: z.string(),
        with: z.string(),
    }),
    mouth: z.object({
        // Legacy 3-state (always present, empty string = feature disabled for MVP).
        closed: z.string(),
        half: z.string(),
        open: z.string(),
        /**
         * Optional 6-viseme set. When present, viseme-driven speech picks from these
         * for more natural lip-sync. When any field is empty the runtime falls back
         * to closed/half/open for that viseme (e.g. `a`/`e`/`o` → open, `i`/`u` →
         * half, everything else → closed).
         *
         * Using the Japanese-animation standard A/I/U/E/O vowel set — maps cleanly
         * to Chinese finals too.
         */
        neutral: z.string().optional(),
        a: z.string().optional(),
        i: z.string().optional(),
        u: z.string().optional(),
        e: z.string().optional(),
        o: z.string().optional(),
    }),
    ai: z.object({
        systemPrompt: z.string().max(8000).default(''),
        searchIndexName: z.string().max(80).default(''),
        voiceName: z.string().max(80).default(''),
        locale: z.string().default('zh-TW'),
        /**
         * LLM provider —
         *   - `'openai'` (default, hits api.openai.com)
         *   - `'custom'` (any OpenAI-compatible endpoint at `baseUrl`, e.g.
         *     self-hosted vLLM / Ollama / LM Studio)
         *   - `'chatbot-bridge'` (third-party chatbot backend that wraps LLM +
         *     flow + CRM behind a `POST /line/bridge` envelope — see
         *     `chatbotBridge` sub-config below + `CUSTOMER_CHAT_KEY_API.md`).
         *
         * When `provider === 'chatbot-bridge'`, the `apiKey` / `baseUrl` /
         * `model` / `useProxy` fields below are IGNORED; the bridge call
         * is configured via `ai.chatbotBridge` exclusively.
         */
        provider: z.enum(['openai', 'custom', 'chatbot-bridge']).default('openai'),
        /**
         * `provider === 'chatbot-bridge'` sub-config. Required when the
         * bridge provider is selected. `clientKey` + `baseUrl` are SECRETS
         * — see `SECRET_PATHS` in admin-api's secret-redaction module;
         * both are stripped from every public-facing serialization
         * (public-info, embed config.json, error bodies).
         */
        chatbotBridge: z.object({
            /** Bridge base URL (e.g. `https://tat.sparklecloud.co/chatbot-backend/api`). */
            baseUrl: z.string().url(),
            /** `X-Chatbot-Client-Key` header value. SECRET — never reaches browser. */
            clientKey: z.string().min(1),
            /** Upstream flow ID. The doc warns `1` is a default that may not match the live flow. */
            chatbotId: z.number().int().positive().default(1),
            /** Upstream source-tag for the customer's analytics. */
            source: z.string().min(1).default('3d-avatar'),
        }).optional(),
        /**
         * Provider-agnostic API key. For OpenAI this is `sk-...`; for Custom
         * endpoints anything the server expects (Ollama accepts arbitrary
         * strings, vLLM typically uses `sk-...`-style). Stored per-character
         * (server-side only — never sent back to the browser).
         */
        apiKey: z.string().max(2048).default(''),
        /**
         * Chat model — provider-specific identifier. OpenAI defaults to
         * `gpt-4o-mini`; Custom must be set explicitly to the model name the
         * upstream serves (e.g. `gemma3:4b`, `Gemma-4-26B-A4B-it-NVFP4A16`).
         */
        model: z.string().max(200).default('gpt-4o-mini'),
        /**
         * Custom-only base URL (e.g. `https://feuera.ngrok.pizza/llm/v1`). The
         * chat endpoint appends `/chat/completions` to this URL. Optional and
         * ignored when `provider === 'openai'`.
         */
        baseUrl: z.string().max(2048).optional(),
        /**
         * Custom-only opt-in: route the chat completion through admin-api's
         * `/api/llm-proxy` server-side fallback (gated by `LLM_PROXY_ENABLED`
         * env var) instead of a direct fetch. Only meaningful for Custom
         * provider; OpenAI always uses direct fetch.
         */
        useProxy: z.boolean().default(false),
        /**
         * Legacy field names. `openaiApiKey` is aliased to `apiKey`,
         * `openaiModel` is aliased to `model` by the tolerant-read normaliser.
         * Kept on the schema so writes from old clients still validate; new
         * code should write to `apiKey` / `model` directly.
         */
        openaiApiKey: z.string().max(2048).optional(),
        openaiModel: z.string().max(200).optional(),
        /**
         * How many of the most recent chat turns the embed sends as
         * `history` on each new turn so the LLM has conversational
         * context. Counts each user OR assistant message as one — so
         * 10 = roughly the last 5 user/AI exchange pairs. Larger values
         * cost more tokens per turn. Default 10; 0 disables (strict
         * one-shot mode).
         */
        contextWindowMessages: z.number().int().min(0).max(50).default(10),
        /**
         * Per `character-persona-extensions` capability — free-text fields
         * the affinity analyzer + chat handler use to bias replies and
         * affinity delta decisions. All three are optional + tolerant-read
         * (old records load with empty strings). See spec for prompt
         * injection semantics.
         */
        persona: z.object({
            /** Things the character enjoys / responds positively to. */
            likes: z.string().max(2000).default(''),
            /** Things that irritate or repel the character. */
            dislikes: z.string().max(2000).default(''),
            /** Topics or phrasings the character reacts strongly to (either way). */
            sensitiveTo: z.string().max(2000).default(''),
        }).optional(),
        /**
         * Operator-supplied custom SYSTEM prompt for the life-tick LLM call.
         * When set, fully replaces the platform default. The user-message
         * portion (personality, mood, scene, recent events, interaction
         * count) is platform-controlled regardless — operators cannot
         * remove the structured state because that's what makes life-tick
         * grounded. See packages/admin-api/src/functions/life-tick-worker.ts
         * for the default and the user-message structure.
         */
        lifeTickPrompt: z.string().max(4000).optional(),
    }),
    /**
     * Shared knowledge block (renderer-agnostic). Populated by the ingestion +
     * consolidation pipeline (Playwright crawler → RAG index → draft system
     * prompt). Optional on read for backward-compat; new characters receive a
     * default-empty block when first written.
     */
    knowledge: z
        .object({
        urls: z.array(z.string().url()).default([]),
        rag: z
            .object({
            indexRef: z.string().max(200).default(''),
            chunkCount: z.number().int().min(0).default(0),
            mergedAt: z.string().datetime().optional(),
        })
            .default({ indexRef: '', chunkCount: 0 }),
        systemPrompt: z.string().max(16000).default(''),
        systemPromptOverride: z.string().max(16000).optional(),
    })
        .default({
        urls: [],
        rag: { indexRef: '', chunkCount: 0 },
        systemPrompt: '',
    }),
    /**
     * Shared voice block. Selects the STT locale and TTS voice/style used by
     * the preview page. Overrides `ai.voiceName` / `ai.locale` when set.
     */
    voice: z
        .object({
        sttLocale: z.string().max(20).default('zh-TW'),
        ttsVoice: z.string().max(80).default(''),
        speakingStyle: z.string().max(40).default(''),
        /**
         * Per `embed-mid-reply-interrupt` spec: lets the user interrupt
         * the AI mid-reply by speaking. Default behaviour (when absent
         * from the record) is "enabled" — applied by publicInfoHandler
         * + the embed bundle's runtime fallback. Operators flip it off
         * for kiosk / scripted characters.
         */
        interruptEnabled: z.boolean().optional(),
        /**
         * Hold time (milliseconds) before interrupt fires. 100–2000.
         * Optional; runtime default 250.
         */
        interruptHoldMs: z.number().int().min(100).max(2000).optional(),
        /**
         * Per-character keyword list. When non-empty AND `interruptEnabled`
         * is true, the embed only aborts the AI's current reply when the
         * user's ASR partial text contains one of these keywords (case-
         * insensitive substring match). Otherwise (default behaviour
         * when this list is empty) the embed never auto-interrupts on
         * speech — TTS audio bleed into the mic doesn't accidentally
         * cut off the AI; the user's speech during AI playback is
         * silently dropped until the AI finishes.
         *
         * Recommended values: ["停", "停止", "別說了", "閉嘴", "stop"].
         */
        interruptKeywords: z.array(z.string().min(1).max(30)).max(20).optional(),
        /**
         * Per `embed-idle-small-talk` spec — three-stage post-reply
         * silence escalation. Field semantics:
         *  - `idleEnabled`: master toggle. Default true (active).
         *  - `idleStage1Ms`: silence after AI finishes speaking before
         *    a gentle in-character nudge fires. Default 12000.
         *  - `idleStage2Ms`: additional silence after stage 1 reply
         *    ends before a stronger "are you there?" prompt. Default 25000.
         *  - `idleStage3Ms`: additional silence after stage 2 before
         *    the AI says goodbye and the call auto-closes. Default 60000.
         * All optional; defaults applied at publicInfoHandler so absent
         * records still get the spec-default behaviour.
         */
        idleEnabled: z.boolean().optional(),
        idleStage1Ms: z.number().int().min(5000).max(60000).optional(),
        idleStage2Ms: z.number().int().min(5000).max(60000).optional(),
        idleStage3Ms: z.number().int().min(5000).max(120000).optional(),
        /**
         * Trigger B — when set AND the uuid resolves in `audioClips`,
         * the stage plays this clip instead of running the LLM-driven
         * idle nudge. Clip-only stages produce no chat-panel text bubble.
         */
        idleStage1ClipId: z.string().uuid().optional(),
        idleStage2ClipId: z.string().uuid().optional(),
        idleStage3ClipId: z.string().uuid().optional(),
        /**
         * TTS provider. `'browser'` (default) routes to the runtime's
         * SpeechSynthesis fallback (Google 小姐-style voice, no real
         * lip-sync). `'voxcpm'` routes to a self-hosted VoxCPM2 server,
         * which returns real PCM audio that drives the analyser-based
         * mouth driver for true syllable-level lip-sync.
         */
        provider: z.enum(['browser', 'voxcpm']).default('browser'),
        /**
         * VoxCPM2 server config. Required iff `provider === 'voxcpm'`,
         * ignored otherwise. Per-character so different characters can
         * speak with different cloned voices on the same install.
         */
        voxcpm: z
            .object({
            baseUrl: z.string().max(300).default('https://feuera.ngrok.pizza/tts/voxcpm'),
            voice: z.string().max(80).default('Feuera'),
            /** Default / fallback style (the admin-saved 預設風格). May be
             *  empty when unset — resolution then falls back to styles[0]. */
            style: z.string().max(80).default('預設'),
            /** voxcpm2-style-autoselect — the selected voice's full style
             *  list, cached at admin voice-list refresh so chat.ts can offer
             *  it to the LLM without hitting the upstream per turn. */
            styles: z.array(z.string().max(80)).max(128).default([]),
            /** DEPRECATED (voxcpm2-style-autoselect) — free-text instruct
             *  degraded quality; no longer sent. Kept for back-compat. */
            instructText: z.string().max(200).default(''),
        })
            .optional(),
        /**
         * ASR (speech-to-text) config. When `enabled === true`, the embed
         * page renders a microphone toggle that streams captured audio
         * through admin-api's `/api/asr/*` relay to the configured
         * upstream Qwen3-ASR server.
         *
         * SERVER-SIDE-ONLY FIELDS: `baseUrl`, `apiKey`, `language` are
         * read by admin-api ONLY. The `publicInfoHandler` (manifest.ts)
         * surfaces only a derived boolean `voice.asrEnabled`. Browser
         * code MUST NEVER receive the upstream URL or credential — see
         * the `secret-isolation` capability spec.
         */
        asr: z
            .object({
            enabled: z.boolean().default(false),
            /**
             * ASR provider. Drives which adapter the
             * voice-conversation-relay loads. Adding new entries
             * (e.g. 'deepgram', 'openai-realtime') is purely additive.
             *
             *   - 'qwen3' — self-hosted Qwen3-ASR via WebSocket.
             *               Streaming partials. Requires `baseUrl`.
             *   - 'openai-whisper' — OpenAI Whisper-1 over the public
             *               api.openai.com transcriptions endpoint.
             *               Non-streaming (no partial subtitles).
             *               Ignores baseUrl. Requires apiKey.
             *   - 'openai-gpt4o-transcribe' — same endpoint as
             *               openai-whisper but with the gpt-4o-transcribe
             *               model. Better quality on noisy / mixed-
             *               language input. Same UX trade-off (no
             *               partials).
             *   - 'openai-gpt4o-mini-transcribe' — fastest + cheapest
             *               of the OpenAI options ($0.003/min). Best
             *               choice for low-latency conversational use.
             */
            provider: z.enum([
                'qwen3',
                'openai-whisper',
                'openai-gpt4o-transcribe',
                'openai-gpt4o-mini-transcribe',
            ]).default('qwen3'),
            /**
             * VAD speech threshold (RMS, 0–1 float). Above this the
             * adapter considers the input "speech." Default 0.04 works
             * for a quiet office; bump to 0.06–0.10 in noisy rooms so
             * fan / keyboard / chatter doesn't latch the mic open.
             */
            vadSpeechRms: z.number().min(0.005).max(0.3).optional(),
            /**
             * VAD silence-after-speech in ms before the adapter commits
             * the utterance to the LLM. Default 500. Bump to 800–1200
             * if you take micro-pauses mid-sentence. Lower (300) for
             * snappier turn-taking in clean environments.
             */
            vadSilenceMs: z.number().min(100).max(3000).optional(),
            /**
             * Upstream ASR server (e.g. https://feuera.ngrok.pro).
             * Required when provider is `qwen3`. Ignored for the
             * OpenAI providers — they hard-code api.openai.com.
             */
            baseUrl: z.string().max(300).default(''),
            /** Bearer / api_key for the upstream. NEVER serialised to clients. */
            apiKey: z.string().max(500).default(''),
            /** Optional language hint sent to the upstream (e.g. "Chinese"). */
            language: z.string().max(40).default(''),
        })
            .optional(),
        /**
         * TTS provider config. Mirrors `asr` shape with a provider enum
         * + per-provider sub-block. Drives the voice-conversation
         * relay's TTS adapter selection.
         *
         * Tolerant-read in repo.ts aliases the legacy `voice.voxcpm`
         * field into `voice.tts.voxcpm2` on load when this block is
         * absent — existing characters keep working without write-time
         * migration.
         */
        tts: z
            .object({
            provider: z.enum(['voxcpm2', 'voai', 'brainserver', 'browser', 'none']).default('browser'),
            /**
             * BrainServer custom-TCP TTS sub-config. Required iff
             * `provider === 'brainserver'`. The admin-api handler opens
             * a raw TCP socket from Lambda to `{host, port}`, sends the
             * `0x07` command + utf-8 text, reads back the WAV body, and
             * normalises it to PCM-16. `label` is operator-facing only
             * (e.g. "小芙 v2.0") — the server doesn't read it.
             */
            brainserver: z
                .object({
                host: z.string().min(1).max(253),
                port: z.number().int().min(1).max(65535),
                label: z.string().max(40).optional(),
            })
                .optional(),
            voxcpm2: z
                .object({
                baseUrl: z.string().max(300).default('https://feuera.ngrok.pizza/tts/voxcpm'),
                voice: z.string().max(80).default('Feuera'),
                style: z.string().max(80).default('預設'),
                // voxcpm2-style-autoselect — cached per-voice style list.
                styles: z.array(z.string().max(80)).max(128).default([]),
                // DEPRECATED — instruct_text no longer sent.
                instructText: z.string().max(200).default(''),
            })
                .optional(),
            /**
             * VOAI (https://connect.voai.ai) commercial TTS sub-config.
             * Required iff `provider === 'voai'`; otherwise ignored.
             *
             * `apiKey` is SECRET — listed in SECRET_PATHS so it never
             * reaches the browser via public-info, embed config.json,
             * or admin-API error bodies. Server-side proxy at
             * `/api/tts-voai/{characterId}/synthesize` attaches the
             * `X-API-Key` header out-of-band.
             *
             * `version` drives the per-version `x-sample-rate` header
             * pin (Classic=44100, Neo=32000, Sota+=24000) — operator
             * does not pick a custom rate in v1.
             *
             * `style_weight` is meaningful for `version === 'Classic'`
             * only; ignored for Neo / Sota+.
             */
            voai: z
                .object({
                apiKey: z.string().max(500).default(''),
                version: z.enum(['Classic', 'Neo', 'Sota+']).default('Neo'),
                speaker: z.string().max(80).default(''),
                style: z.string().max(80).default(''),
                speed: z.number().min(0.5).max(1.5).default(1),
                pitch_shift: z.number().min(-5).max(5).default(0),
                style_weight: z.number().min(0).max(1).default(0.5),
                breath_pause: z.number().min(0).max(10).default(0),
                outputFormat: z.enum(['wav', 'mp3']).default('wav'),
            })
                .optional(),
        })
            .optional(),
    })
        .default({ sttLocale: 'zh-TW', ttsVoice: '', speakingStyle: '', provider: 'browser' }),
    /**
     * Per `embed-floating-widget` capability — customer-side integration
     * config. Persisted on the character record, written into the deployed
     * `config.json`, and read by the loader script on the customer's
     * page to decide how to render the widget.
     */
    embed: z
        .object({
        integration: z
            .object({
            /** Visibility mode for the floating widget:
             *  - `'floating'` (default) — renders the corner button + expand panel
             *  - `'iframe-only'` — loader exits; operator embeds the iframe themselves
             *  - `'disabled'` — loader exits silently (URL still accessible) */
            mode: z.enum(['floating', 'iframe-only', 'disabled']).default('floating'),
            /** What mode the iframe launches in when expanded. */
            defaultLaunchMode: z.enum(['text', 'voice']).default('text'),
            /** Floating button icon source. */
            icon: z.union([
                z.object({ kind: z.literal('character-3d') }),
                z.object({ kind: z.literal('custom-image'), url: z.string().url() }),
            ]).default({ kind: 'character-3d' }),
            /** Corner placement of the floating button. */
            position: z.enum(['bottom-right', 'bottom-left']).default('bottom-right'),
            /** Hex accent color — used for the loading spinner and (when
             *  `useAccentBackground` is true) the floating button background.
             *  When `useAccentBackground` is false (default), the button is
             *  fully transparent and the accent color only colours the
             *  loading spinner arc. */
            accentColor: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'must be a 6-digit hex color').default('#4f46e5'),
            /** When true, the floating button has a circular `accentColor`
             *  background behind the 3D character (legacy Intercom-style).
             *  When false (default), the button is fully transparent so the
             *  character appears to "float" — matches the current 2026-05-13
             *  redesign. Toggle in admin UI lets operators bring back the
             *  colored circle if they prefer that style. */
            useAccentBackground: z.boolean().default(false),
            /**
             * Per operator request 2026-05-14: opt-in to a dedicated
             * CloudFront distribution for this character instead of
             * the shared embeds-distribution path-based tenant. Costs
             * +$1.20/month minimum per character, adds 15-30 min to
             * first-deploy, hits the AWS 200-distribution account cap.
             * Use only when the customer needs a fully isolated CDN
             * for billing-attribution or compliance reasons that the
             * shared-distribution Athena-log split can't satisfy.
             *
             * Provisioning is NOT yet implemented — flag is stored
             * but `POST /deploy` refuses with `DEDICATED_CDN_NOT_AVAILABLE`
             * until the per-tenant CloudFormation pipeline lands.
             */
            dedicatedCdn: z.boolean().default(false),
            /**
             * Per operator request 2026-05-14: per-tenant override of
             * the chat bubble + input field colors. Each entry is a
             * 6-digit hex (no alpha — runtime applies its own opacity).
             * Absent fields fall back to the embed-runtime built-in
             * defaults (operator's current style — see EmbedRuntime).
             */
            colors: z.object({
                aiBubbleBg: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
                aiBubbleText: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
                userBubbleBg: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
                userBubbleText: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
                inputBg: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
                inputText: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
            }).optional(),
        })
            .optional(),
        /**
         * Per `embed-snippet-anti-abuse` capability — tenant-scoped abuse
         * resistance for the public embed snippet. All fields back-compat
         * default to "unenforced" so existing characters keep working:
         *  - `originAllowlist` empty / absent → no Origin check
         *  - `snippetToken` null / absent → no token check
         *  - `chatRateLimitPerMin` absent → default 30/min/IP
         *
         * Operator activates per-character from the admin Security panel.
         */
        security: z
            .object({
            originAllowlist: z.array(z.string().url()).default([]),
            snippetToken: z
                .object({
                version: z.number().int().positive(),
                issuedAt: z.string().datetime(),
            })
                .nullable()
                .default(null),
            chatRateLimitPerMin: z.number().int().min(10).max(120).optional(),
        })
            .optional(),
    })
        .optional(),
    /**
     * Preview gating — optional PIN required on `/p/:slug`. When unset the
     * preview route is reachable without authentication.
     */
    preview: z
        .object({
        pin: z.string().max(40).optional(),
    })
        .default({}),
    /**
     * Per `character-access-policy` capability — master toggle for the
     * fan-management feature surface. Default 'disabled' (absent on
     * legacy records is treated identically) preserves the existing
     * customer-service embed flow: anonymous chat, no wallet, no memory.
     * Operators flip to 'enabled' per character to opt in to fan login +
     * ACL + wallet + memory.
     */
    fanMode: z.enum(['disabled', 'enabled']).optional(),
    /**
     * Per `character-delisting` capability — character lifecycle status.
     * `active` (default; absent on legacy records is treated identically)
     * means the character is live. `delisted` (下架) means it is hidden
     * from the fan front-stage AND every fan/communication feature
     * (chat / voice / ASR / TTS / acquire / alarm / character-bound
     * point-card redeem) refuses it with 410 CHARACTER_DELISTED. This is
     * ORTHOGONAL to `fanMode`: a fanMode-enabled character can be
     * temporarily delisted and later relisted into the same config.
     * Delisting never touches wallet balances or any history rows — it is
     * a pure, reversible state flip. Superadmin-only.
     *
     * Optional with NO default (mirrors `fanMode`): an absent value is
     * treated identically to `'active'` by `isDelisted()` and every
     * guard — so legacy records and skeleton/fixture constructors don't
     * have to set it, and the output type stays assignable.
     */
    listingStatus: z.enum(['active', 'delisted']).optional(),
    /** ISO timestamp set when the character was delisted; cleared on relist. */
    delistedAt: z.string().datetime().optional(),
    /** Optional operator-supplied reason recorded at delist time. */
    delistedReason: z.string().max(280).optional(),
    /**
     * Operator-facing usage gate. Absent / true means usable. false keeps the
     * character visible in admin galleries but marks it locked/unusable.
     */
    usageEnabled: z.boolean().optional(),
    /**
     * Per `fan-game-bridge` — external mini-game metadata (rarity/roles/
     * motion overrides + system-cached GLB sha256). Absent → game defaults.
     */
    gameProfile: GameProfileSchema.optional(),
    /**
     * Per-character affinity tier configuration. When absent or empty,
     * the platform defaults (`TIER_NAMES_V2` / `TIER_DESCRIPTIONS_V2`)
     * are used. When present MUST be exactly 10 entries (tier 1-10);
     * any other length is rejected by the operator save handler.
     *
     * The `name` is what fans see in the AffinityBar and what gets
     * injected into chat.ts as the AI's "current relationship label"
     * for this character. The `description` is shown when the fan taps
     * the bar for an explainer — let the operator phrase what each
     * tier *feels* like for this specific character (e.g., a doctor
     * character might use clinical relationship terms; a hostess
     * might use romantic ones).
     */
    affinityTiers: z.array(z.object({
        name: z.string().min(1).max(40),
        description: z.string().min(0).max(200),
    })).length(10).optional(),
    /**
     * Cognito sub of the operator who owns this character. Used by the
     * fan-wallet stack to surface the operator's currency to fans (chat
     * spend, fan-acquire wallet pre-create, /wallet GET enrichment).
     *
     * Auto-assigned on first authenticated character save (per the
     * characters update handler in admin-api). Empty for characters
     * created via admin-token sessions or legacy records pre-dating
     * this field.
     */
    customerId: z.string().min(1).optional(),
    /**
     * Per `fan-youtube-integration` — operator-declared binding of this
     * character to a YouTube channel. Setting `channelId` alone makes
     * the character page render a "前往 YouTube 頻道" affordance; the
     * separate operator-side YouTube OAuth flow toggles
     * `membersFeatureStatus` from 'disabled' → 'pending-first-poll' →
     * 'active' once the member-list poller runs.
     */
    youtubeChannel: YouTubeChannelBindingSchema.optional(),
    /**
     * Per `fan-wallet-and-points` capability — LEGACY single-group field.
     * Kept readable so existing characters still gate correctly until the
     * operator migrates them to `walletGroupIds`. New writes should set
     * `walletGroupIds` instead; this field is read-only after migration.
     */
    walletGroupId: z.string().uuid().optional(),
    /**
     * Per `walletGroupIds-multi-group` change — which fan groups can
     * self-enroll into this character (mode='public' enrollment gate).
     * Fan needs to be a member of ANY one of these groups to be allowed
     * without re-enrollment; otherwise self-enroll picks one of them.
     *
     * Backward compatibility: when this array is absent the access-policy
     * falls back to the legacy `walletGroupId` singleton. Setting at least
     * one entry here makes the legacy field vestigial.
     */
    walletGroupIds: z.array(z.string().uuid()).optional(),
    /**
     * Per `character-gallery` change — operator-uploaded image gallery
     * (banner + promo photos + AI-card reservation). Each entry has one
     * of three statuses:
     *   - 'published':   shown to fans on the character page banner slot
     *   - 'hidden':      soft-deleted, operator can still see/restore
     *   - 'card':        reserved for the future character-card AI image
     *                    generation flow; never surfaced to fans for now
     *   - 'maintenance': swapped onto banner + chat overlay when this
     *                    character has any open incident (upstream TTS/
     *                    LLM/ASR failure). Operator should upload one
     *                    per character; on incident the fan SPA picks
     *                    the lowest-sortOrder maintenance image.
     *
     * sortOrder drives the display order on the fan side; lower first.
     * The S3 object lives under <assets-bucket>/characters/<id>/gallery/
     * <imageId>.<ext>; `url` is the CloudFront URL for direct display.
     */
    gallery: z
        .array(z.object({
        imageId: z.string(),
        s3Key: z.string(),
        url: z.string(),
        status: z.enum(['published', 'hidden', 'card', 'maintenance']),
        mimeType: z.string(),
        sizeBytes: z.number().int().nonnegative(),
        width: z.number().int().positive().optional(),
        height: z.number().int().positive().optional(),
        uploadedAt: z.string(),
        caption: z.string().max(500).optional(),
        sortOrder: z.number().int().optional(),
    }))
        .optional(),
    /**
     * Per `fan-wallet-and-points` capability — pricing overrides per
     * character. Absent fields fall through to platform env defaults
     * (DEFAULT_CHAT_TEXT_COST=1 / DEFAULT_CHAT_VOICE_COST=3).
     */
    chatPricing: z
        .object({
        text: z.number().int().min(1).max(100).optional(),
        voice: z.number().int().min(1).max(300).optional(),
        /**
         * Per `chrome-extension-page-summarize` capability — per-call
         * cost for the extension's POST /api/ext/.../summarize. Page
         * summaries typically cost 10-20x a chat turn in LLM tokens
         * so operators may want to charge more. Falls back to `text`
         * (then to env default) when unset.
         */
        summarize: z.number().int().min(0).max(500).optional(),
    })
        .optional(),
    /**
     * Per `chrome-extension-desktop-pet` capability — extra suffix appended
     * to `ai.systemPrompt` for chat/summarize requests originating from
     * the Chrome extension pet overlay. The pet is a small floating UI
     * with limited bubble width, so responses need to be short. Default
     * (empty/unset) uses a built-in "請以 2-3 句話內精簡回應" fallback.
     * Editable in admin so each character can tune brevity vs personality
     * for the extension surface without affecting the fan SPA chat.
     */
    extensionPromptSuffix: z.string().max(500).optional(),
    /**
     * Per the fan-platform monetisation alignment — operator can offer
     * the character's 3D model file for download (free) or sale (paid).
     * Visible only on fanMode-enabled characters; relevant primarily for
     * public-mode characters where any enrolled fan might want a personal
     * copy of the model.
     *
     * - mode='free': any enrolled fan can fetch a presigned download URL.
     * - mode='paid': fan must purchase first (ECPay flow, currently
     *   stubbed — backend gate exists, ECPay redirect is TODO until
     *   ecpay-payment-integration's stub becomes real).
     *
     * `priceTwd` is required + validated when mode='paid'.
     */
    /**
     * Skin / model distribution (free, paid, or coming soon). "Skin" in operator
     * terminology = the character's 3D model + appearance package.
     * Owning the skin unlocks full 3D interactivity on the fan platform;
     * NOT owning shows the preview overlay. `coming-soon` remains visible
     * in discovery and blocks new acquisition while preserving access for
     * fans who already own the character.
     *
     * Pricing is in **platform points** (黑耀石) — NOT NTD. Money only
     * enters the system via topup; everything else is point-denominated.
     * Field name `pricePoints` (was `priceTwd`) signals this.
     */
    modelDistribution: z
        .object({
        enabled: z.boolean(),
        mode: z.enum(['free', 'paid', 'coming-soon']),
        pricePoints: z.number().int().min(1).max(100000).optional(),
    })
        .superRefine((d, ctx) => {
        if (d.mode === 'paid' && (d.pricePoints === undefined || d.pricePoints < 1)) {
            ctx.addIssue({ code: 'custom', path: ['pricePoints'], message: 'paid mode requires pricePoints >= 1' });
        }
    })
        .optional(),
    /**
     * Per `character-access-policy` capability — which fans may chat with
     * this character when fanMode is 'enabled'. Default (absent) is
     * `mode: 'public'` = any logged-in fan can chat. When fanMode is
     * 'disabled' this field has NO effect regardless of value.
     *
     *   mode='public'     — any fan can chat (anonymous → 401)
     *   mode='group'      — only fans in one of `groupIds` can chat
     *   mode='individual' — only fans whose sub is in `fanIds` can chat
     */
    accessPolicy: z
        .object({
        mode: z.enum(['public', 'group', 'individual']),
        groupIds: z.array(z.string().uuid()).max(50).optional(),
        fanIds: z.array(z.string().min(1).max(64)).max(5000).optional(),
    })
        .superRefine((policy, ctx) => {
        if (policy.mode === 'public') {
            if (policy.groupIds && policy.groupIds.length > 0) {
                ctx.addIssue({ code: 'custom', path: ['groupIds'], message: 'public mode must not specify groupIds' });
            }
            if (policy.fanIds && policy.fanIds.length > 0) {
                ctx.addIssue({ code: 'custom', path: ['fanIds'], message: 'public mode must not specify fanIds' });
            }
        }
        else if (policy.mode === 'group') {
            if (!policy.groupIds || policy.groupIds.length === 0) {
                ctx.addIssue({ code: 'custom', path: ['groupIds'], message: 'group mode requires non-empty groupIds' });
            }
            if (policy.fanIds && policy.fanIds.length > 0) {
                ctx.addIssue({ code: 'custom', path: ['fanIds'], message: 'group mode must not specify fanIds' });
            }
        }
        else if (policy.mode === 'individual') {
            if (!policy.fanIds || policy.fanIds.length === 0) {
                ctx.addIssue({ code: 'custom', path: ['fanIds'], message: 'individual mode requires non-empty fanIds' });
            }
            if (policy.groupIds && policy.groupIds.length > 0) {
                ctx.addIssue({ code: 'custom', path: ['groupIds'], message: 'individual mode must not specify groupIds' });
            }
        }
    })
        .optional(),
    /**
     * Per `fan-affinity-system` iter 31 — operator-uploaded gifts the
     * character "gives" the fan when they cross each affinity level.
     * Keys are level numbers 1-5; values are { stickerUrl?, voiceUrl?,
     * customMessage? } — at least one field must be present per entry.
     * Operator-side upload UI is a separate work-stream; this schema
     * field is the data contract. Absent entries fall back to the
     * built-in defaults from lib/fan-affinity.ts unlocksUpToLevel.
     */
    fanLevelGifts: z
        .record(z.enum(['1', '2', '3', '4', '5']), z.object({
        stickerUrl: z.string().url().max(500).optional(),
        voiceUrl: z.string().url().max(500).optional(),
        customMessage: z.string().min(1).max(400).optional(),
    }).refine((g) => !!g.stickerUrl || !!g.voiceUrl || !!g.customMessage, { message: '每個禮物至少要有 sticker、voice 或 customMessage 其中一項' }))
        .optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    /**
     * Per-tenant URL segment — derived from slug/name on first deploy
     * via `POST /api/characters/:id/deploy`. The CloudFront-served
     * embed URL is `https://<embedsCloudFrontDomain>/<tenantSlug>/`.
     * Once persisted, subsequent re-deploys reuse the stored value so
     * the public URL stays stable even when the slug changes.
     */
    tenantSlug: z.string().max(40).optional(),
    /**
     * Operator-defined hashtags. Surfaced as chips on the characters
     * gallery card and will drive future sort / filter UX. Free-form
     * strings (no enum) so the operator can spin up new categories
     * without a schema migration. Capped at 10 tags × 30 chars to keep
     * the card layout sane and stop accidental novel-length entries.
     */
    tags: z.array(z.string().min(1).max(30)).max(10).optional(),
    /**
     * Operator-controlled manual sort key on the Characters gallery.
     * 1–9999 inclusive; lower numbers appear first. Absent = sort by
     * updatedAt as fallback (controlled by the gallery's mode toggle).
     * Doesn't change any URL / API behaviour — pure UX ordering.
     */
    sortOrder: z.number().int().min(1).max(9999).optional(),
    /**
     * Per `character-custom-audio-clips`: operator-uploaded library of
     * pre-recorded mp3/wav/ogg clips. Each entry carries enough metadata
     * to (a) play through `attachAudio` for lip-sync, (b) be referenced
     * from greeting / keywordResponses / idle-stage triggers, and (c)
     * appear in the chat handler's structured-output catalog when
     * `aiVisible` is true. Capped at 30 per character.
     */
    audioClips: z.array(z.object({
        id: z.string().uuid(),
        label: z.string().min(1).max(60),
        url: z.string().url(),
        blobPath: z.string().min(1).max(300),
        contentType: z.enum(['audio/mpeg', 'audio/wav', 'audio/ogg']),
        durationSec: z.number().positive().optional(),
        transcript: z.string().max(400).default(''),
        aiVisible: z.boolean().default(false),
        /**
         * Fallback trigger — when none of `keywordResponses` matches the
         * user message, the chat handler plays this clip and skips the LLM.
         * At most one clip in the library may have this set (refine below);
         * otherwise the chat would have multiple equally-valid fallbacks
         * with no deterministic priority.
         */
        playOnNoKeywordMatch: z.boolean().default(false),
    })).max(30).default([])
        .refine((clips) => clips.filter((c) => c.playOnNoKeywordMatch).length <= 1, { message: '只能有一個音檔被設為「未匹配關鍵字時播放」' }),
    /**
     * Trigger C — synthetic AI turn played on every widget mount when
     * `enabled` is true. When `clipId` resolves to an entry in
     * `audioClips`, the runtime plays that clip via the
     * `playCustomAudio` action and skips TTS for the greeting;
     * otherwise the configured TTS provider voices `text`.
     */
    greeting: z.object({
        enabled: z.boolean().default(false),
        text: z.string().max(400).default(''),
        clipId: z.string().uuid().optional(),
    }).optional(),
    /**
     * Per `alarm-vtuber-api` — config for the external AI Alarm VTuber app.
     * Only meaningful when fanMode is 'enabled' (gated in the admin UI +
     * server). `enabled` exposes the character to the alarm app's
     * fan-scoped roster. `moods` are 6 operator-uploaded立繪 (CDN URLs);
     * a missing key falls back to the character thumbnail. `version` /
     * `templateVersion` drive the assets / prompt-template ETags.
     * Additive, default-absent → not alarm-enabled.
     */
    alarm: z.object({
        enabled: z.boolean().default(false),
        version: z.string().max(20).default('1.0.0'),
        templateVersion: z.string().max(20).default('v1'),
        promptTemplate: z.string().max(2000).default(''),
        /** Points consumed per wake call (= one conversation). Debited from
         *  the fan's operator-scope wallet, same as a chat turn. Default 1;
         *  0 = free. Editable in the admin 鬧鐘 settings. */
        costPoints: z.number().int().min(0).max(1000).default(1),
        /** Where the wake audio comes from:
         *   - `ai`    : compose prompt → character LLM → character TTS (fresh take)
         *   - `clips` : pick a random operator-uploaded clip (no LLM/TTS) */
        voiceSource: z.enum(['ai', 'clips']).default('ai'),
        /** Operator-uploaded wake clips (for `voiceSource: 'clips'`). The wake
         *  endpoint picks one at random. Mirrors the audio-clips feature. */
        clips: z.array(z.object({
            id: z.string().min(1).max(64),
            url: z.string().url(),
            label: z.string().max(80).optional(),
        })).max(30).default([]),
        moods: z.object({
            morning: z.string().url().optional(),
            afternoon: z.string().url().optional(),
            evening: z.string().url().optional(),
            late_night: z.string().url().optional(),
            alarm_just_set: z.string().url().optional(),
            birthday: z.string().url().optional(),
        }).default({}),
        livery: z.object({
            name: z.string().max(40).default('default'),
            startsAt: z.string().datetime().nullable().optional(),
            endsAt: z.string().datetime().nullable().optional(),
        }).optional(),
    }).optional(),
    /**
     * Per-character audio intent rules. Legacy `contains` and deterministic
     * `normalized` matches short-circuit before the LLM; `ai` rules are
     * exposed to the model so conversational intent decides playback.
     */
    keywordResponses: z.array(z.object({
        id: z.string().uuid(),
        keyword: z.string().min(1).max(60),
        text: z.string().max(400),
        clipId: z.string().uuid().optional(),
        matchMode: z.enum(['contains', 'normalized', 'ai']).default('contains'),
    })).max(20).default([]),
    /**
     * ISO timestamp of the most recent successful deploy. The admin
     * UI's Deploy panel compares this to `updatedAt` to decide whether
     * the live tenant is stale (character changed since last deploy).
     * Absent until the first deploy completes.
     */
    lastDeployedAt: z.string().datetime().optional(),
    /**
     * Optional v2 PNGTuber manifest payload (ManifestV2 shape — see
     * `@nxorbit/pngtuber-runtime` types: layerTree + user-defined states +
     * showWhen + wiggle + physics + scream). Stored opaquely here; the
     * admin-api does not re-validate deeply — the runtime's
     * `validateManifestV2` runs on mount and surfaces structural errors in
     * the UI. When set on a PNGTuber-type character, Overview + Preview
     * mount the v2 runtime instead of the v1 layer-compose renderer.
     *
     * Coexists with the legacy v1 fields (layers / states / blink / mouth)
     * so migration is strictly additive.
     */
    pngtuberV2Manifest: z.any().optional(),
    /**
     * Optional v2 skeletal rig. When set, this character uses the rig renderer
     * (part tree + parameters + bindings) instead of the legacy layer-compose
     * runtime. Both formats coexist in the same document so migration is
     * non-breaking.
     */
    rig: CharacterRigSchema.optional(),
    /**
     * Optional agentic-AI DAG (slice 1 of 6). Schema-only here — the chat
     * handler returns 501 NOT_IMPLEMENTED when `enabled === true` until
     * the executor lands in slice 2. Existing characters omit this field
     * and continue using the legacy single-LLM-call chat path.
     */
    agenticGraph: AgenticGraphSchema.optional(),
    /**
     * Per embed-conversation-shortcuts spec — optional quick-reply FAB
     * shown in the public embed. Absent → no FAB rendered. Categories /
     * items capped by the operator-tunable `maxCategories` /
     * `maxItemsPerCategory` fields; cross-field cap check enforced in
     * refine() below.
     */
    shortcuts: ShortcutsSchema.optional(),
});
function refine(strict) {
    return (c, ctx) => {
        if (strict && !slugPattern.test(c.slug)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['slug'], message: 'slug must be 3–40 lowercase alphanumeric/hyphen chars' });
        }
        // Discriminator payload consistency. PNGTuber's payload is the root-level
        // layers/states/mouth/blink shape; live2d/model3d have their own sibling
        // blocks. The matching block MUST be present for non-PNGTuber types, and
        // non-matching blocks MUST be absent for every type.
        if (c.type === 'live2d') {
            if (!c.live2d)
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['live2d'], message: 'live2d payload required when type=live2d' });
            if (c.model3d)
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['model3d'], message: 'model3d must be absent when type=live2d' });
        }
        else if (c.type === 'model3d') {
            if (!c.model3d)
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['model3d'], message: 'model3d payload required when type=model3d' });
            if (c.live2d)
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['live2d'], message: 'live2d must be absent when type=model3d' });
        }
        else {
            // type === 'pngtuber' — no sibling payloads allowed
            if (c.live2d)
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['live2d'], message: 'live2d must be absent when type=pngtuber' });
            if (c.model3d)
                ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['model3d'], message: 'model3d must be absent when type=pngtuber' });
        }
        const layerKeys = new Set(Object.keys(c.layers));
        const check = (name, path) => {
            if (name === '__mouth__')
                return;
            if (!strict && name === '')
                return;
            if (!layerKeys.has(name)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, path, message: `unknown layer "${name}"` });
            }
        };
        check(c.mouth.closed, ['mouth', 'closed']);
        check(c.mouth.half, ['mouth', 'half']);
        check(c.mouth.open, ['mouth', 'open']);
        // Optional visemes: only validate when set (undefined = not configured).
        const visemeKeys = ['neutral', 'a', 'i', 'u', 'e', 'o'];
        for (const k of visemeKeys) {
            const v = c.mouth[k];
            if (v !== undefined)
                check(v, ['mouth', k]);
        }
        check(c.blink.replaces, ['blink', 'replaces']);
        check(c.blink.with, ['blink', 'with']);
        if (!c.states[c.initialState]) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['initialState'],
                message: 'not in states',
            });
        }
        for (const [sname, s] of Object.entries(c.states)) {
            s.compose.forEach((n, i) => check(n, ['states', sname, 'compose', i]));
        }
        const [lo, hi] = c.blink.intervalMs;
        if (lo > hi) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['blink', 'intervalMs'],
                message: 'min > max',
            });
        }
        // Agentic graph DAG validation. The zod sub-schema enforces shape
        // (id pattern, type enum, non-empty nodes / exits); the structural
        // checks (id-uniqueness, edge target existence, cycle detection)
        // live in `validateAgenticDag` so the executor can reuse them.
        if (c.agenticGraph) {
            for (const issue of validateAgenticDag(c.agenticGraph)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['agenticGraph', ...issue.path],
                    message: issue.message,
                });
            }
        }
        // character-custom-audio-clips: every clipId referenced from
        // greeting / keywordResponses / idle stages MUST resolve to a
        // uuid in `audioClips`. Dangling refs would surface as silent
        // playback failures at runtime — surface them at validation time
        // so the admin UI can pin the error to the offending input.
        const clipIds = new Set((c.audioClips ?? []).map((c2) => c2.id));
        const checkClipRef = (id, path) => {
            if (id && !clipIds.has(id)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path,
                    message: `clipId "${id}" not found in audioClips`,
                });
            }
        };
        checkClipRef(c.greeting?.clipId, ['greeting', 'clipId']);
        (c.keywordResponses ?? []).forEach((kr, i) => {
            checkClipRef(kr.clipId, ['keywordResponses', i, 'clipId']);
        });
        const v = c.voice;
        if (v) {
            checkClipRef(v.idleStage1ClipId, ['voice', 'idleStage1ClipId']);
            checkClipRef(v.idleStage2ClipId, ['voice', 'idleStage2ClipId']);
            checkClipRef(v.idleStage3ClipId, ['voice', 'idleStage3ClipId']);
        }
        // brainserver-tts-provider: provider === 'brainserver' requires the
        // sibling sub-config (host + port). Sub-config is meaningless without
        // these two so we surface the missing field at validation time
        // rather than letting the embed runtime call a half-configured route.
        if (c.voice?.tts?.provider === 'brainserver' && !c.voice.tts.brainserver) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['voice', 'tts', 'brainserver'],
                message: 'voice.tts.brainserver (host + port) is required when provider === "brainserver"',
            });
        }
        // embed-conversation-shortcuts: enforce the operator-tunable caps
        // (the absolute hard bounds 6 / 12 are enforced by the inner
        // schemas; this is the per-character soft cap).
        if (c.shortcuts) {
            const { maxCategories, maxItemsPerCategory, categories } = c.shortcuts;
            if (categories.length > maxCategories) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['shortcuts', 'categories'],
                    message: `categories.length (${categories.length}) exceeds maxCategories (${maxCategories})`,
                });
            }
            categories.forEach((cat, i) => {
                if (cat.items.length > maxItemsPerCategory) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['shortcuts', 'categories', i, 'items'],
                        message: `items.length (${cat.items.length}) exceeds maxItemsPerCategory (${maxItemsPerCategory})`,
                    });
                }
            });
        }
        // scene-objects-and-attachments cross-field validation
        if (c.model3d?.sceneObjects) {
            c.model3d.sceneObjects.forEach((obj, i) => {
                const base = ['model3d', 'sceneObjects', i];
                if (obj.kind === 'dynamic' && !obj.attachment?.boneName) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [...base, 'attachment'],
                        message: 'attachment.boneName is required when kind === "dynamic"',
                    });
                }
                if (obj.kind === 'static' && obj.attachment) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [...base, 'attachment'],
                        message: 'attachment must not be set when kind === "static"',
                    });
                }
                if (obj.visibility === 'ai-controlled' && (!obj.aiDescription || obj.aiDescription.trim().length === 0)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: [...base, 'aiDescription'],
                        message: 'aiDescription is required when visibility === "ai-controlled"',
                    });
                }
            });
        }
    };
}
export const CharacterSchema = baseShape.superRefine(refine(true));
export const DraftCharacterSchema = baseShape.superRefine(refine(false));
/**
 * Per `character-delisting` capability — true when the character is
 * delisted (下架). Treats an absent `listingStatus` (legacy records) as
 * `active`. The throwing `requireListed()` guard lives in admin-api
 * `lib/delisting.ts`; this is the pure predicate shared with any caller
 * (e.g. fan-discovery filtering) that just needs the boolean.
 */
export function isDelisted(c) {
    return c?.listingStatus === 'delisted';
}
export const DEFAULT_EMBED_INTEGRATION = {
    mode: 'floating',
    defaultLaunchMode: 'text',
    icon: { kind: 'character-3d' },
    position: 'bottom-right',
    accentColor: '#4f46e5',
    useAccentBackground: false,
};
export const DEFAULT_EMBED_SECURITY = {
    originAllowlist: [],
    snippetToken: null,
};
// ── Secret-isolation pattern ────────────────────────────────────────────
//
// API-key inputs in the admin UI follow a write-only convention: when a
// draft loads from the server, the input renders this fixed-length
// placeholder so the persisted value never re-flows back into the form.
// On Save, repo.ts detects the sentinel and preserves the existing
// stored value (i.e. "user didn't touch this field"); any other value
// overwrites.
//
// The sentinel is a Unicode bullet sequence — chosen so it can't
// realistically collide with a real API key (no real key is "8
// bullets"). Length-only signal of "is this configured?" without
// exposing length information about the actual secret either.
export const SECRET_SENTINEL = '••••••••';
export function isSecretSentinel(s) {
    return s === SECRET_SENTINEL;
}
